import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Trash2, Plus, Edit, GraduationCap, X } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

interface Announcement {
  id: string;
  title: string;
  content: string;
  published: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  translations?: Record<string, AnnouncementTranslation>;
}

interface AnnouncementTranslation {
  announcement_id: string;
  language: string;
  title: string;
  content: string;
}

interface Course {
  id: string;
  title: string;
  published: boolean;
}

export const AnnouncementsManagement = () => {
  const { user, isAdmin, isAnnouncementEditor } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft">("all");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    content: "",
    published: false,
  });
  const { toast } = useToast();
  const { t, language } = useLanguage();

  // Filter announcements based on status
  const filteredAnnouncements = announcements.filter(announcement => {
    if (statusFilter === "published") return announcement.published;
    if (statusFilter === "draft") return !announcement.published;
    return true; // "all"
  });

  useEffect(() => {
    fetchAnnouncements();
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const { data, error } = await supabase
        .from("courses")
        .select("id, title, published")
        .eq("published", true)
        .order("title");

      if (error) throw error;
      setCourses(data || []);
    } catch (error: any) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("announcements")
        .select("*");
      
      // Filter by role: announcement editors can only see their own content
      if (isAnnouncementEditor && !isAdmin) {
        query = query.eq('created_by', user?.id);
      }
      
      const { data: announcementData, error: announcementError } = await query.order("created_at", { ascending: false });

      if (announcementError) throw announcementError;

      const { data: translationsData, error: translationsError } = await supabase
        .from("announcements_translations")
        .select("*");

      if (translationsError) throw translationsError;

      const announcementsWithTranslations = (announcementData || []).map(announcement => {
        const announcementTranslations: Record<string, AnnouncementTranslation> = {};
        (translationsData || [])
          .filter(t => t.announcement_id === announcement.id)
          .forEach(t => {
            announcementTranslations[t.language] = t;
          });
        return { ...announcement, translations: announcementTranslations };
      });

      setAnnouncements(announcementsWithTranslations);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAnnouncement = async () => {
    try {
      setIsTranslating(true);
      setTranslationProgress(10);
      
      let announcementId: string;
      
      if (editingAnnouncement) {
        announcementId = editingAnnouncement.id;
        setTranslationProgress(20);
        // Only update the main table if editing in English
        if (language === 'en') {
          const { error } = await supabase
            .from("announcements")
            .update(announcementForm)
            .eq("id", editingAnnouncement.id);

          if (error) throw error;
        } else {
          // For non-English, only update published status in main table
          const { error } = await supabase
            .from("announcements")
            .update({ published: announcementForm.published })
            .eq("id", editingAnnouncement.id);

          if (error) throw error;
        }

        setTranslationProgress(30);

        // Auto-translate to all languages (including current language for consistency)
        try {
          const targetLanguages = ['en', 'ru', 'es'];
          
          if (targetLanguages.length > 0) {
            setTranslationProgress(40);
            // Translate title and content
            console.log('Starting translations for languages:', targetLanguages);
            
            const { data: titleTranslations, error: titleError } = await supabase.functions.invoke('translate-content', {
              body: {
                text: announcementForm.title,
                fromLanguage: language,
                toLanguages: targetLanguages
              }
            });

            setTranslationProgress(60);

            if (titleError) {
              console.error('Title translation error:', titleError);
            } else {
              console.log('Title translations received:', titleTranslations);
            }

            const { data: contentTranslations, error: contentError } = await supabase.functions.invoke('translate-content', {
              body: {
                text: announcementForm.content,
                fromLanguage: language,
                toLanguages: targetLanguages
              }
            });

            setTranslationProgress(80);

            if (contentError) {
              console.error('Content translation error:', contentError);
            } else {
              console.log('Content translations received:', contentTranslations);
            }

            if (!titleError && !contentError && titleTranslations?.translations && contentTranslations?.translations) {
              // Save translations for each language
              for (const lang of targetLanguages) {
                console.log(`Saving ${lang} translation:`, {
                  title: titleTranslations.translations[lang],
                  content: contentTranslations.translations[lang]
                });
                
                const { error: upsertError } = await supabase
                  .from("announcements_translations")
                  .upsert({
                    announcement_id: editingAnnouncement.id,
                    language: lang,
                    title: titleTranslations.translations[lang],
                    content: contentTranslations.translations[lang],
                  }, {
                    onConflict: 'announcement_id,language'
                  });
                
                if (upsertError) {
                  console.error(`Error saving ${lang} translation:`, upsertError);
                }
              }
            } else {
              console.error('Translation data incomplete:', { titleError, contentError, titleTranslations, contentTranslations });
            }
          }

          setTranslationProgress(90);

          // Update the current language translation if not English
          if (language !== 'en') {
            console.log(`Saving current language (${language}) translation`);
            const { error: currentLangError } = await supabase
              .from("announcements_translations")
              .upsert({
                announcement_id: editingAnnouncement.id,
                language: language,
                title: announcementForm.title,
                content: announcementForm.content,
              }, {
                onConflict: 'announcement_id,language'
              });
            
            if (currentLangError) {
              console.error(`Error saving current language (${language}):`, currentLangError);
            }
          }
        } catch (translationError) {
          console.error('Translation error:', translationError);
          // Continue even if translation fails
        }

        setTranslationProgress(100);
        toast({ title: t("admin.success"), description: t("admin.announcements.updatedsuccess") });
        
        // Update course relationships
        await saveCourseRelationships(announcementId);
      } else {
        setTranslationProgress(20);
        const { data: newAnnouncement, error } = await supabase
          .from("announcements")
          .insert([{
            ...announcementForm,
            created_by: user?.id
          }])
          .select()
          .single();

        if (error) throw error;

        announcementId = newAnnouncement.id;
        setTranslationProgress(30);

        // Auto-translate to other languages
        if (newAnnouncement) {
          try {
            const targetLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
            
            if (targetLanguages.length > 0) {
              setTranslationProgress(40);
              console.log('Starting translations for new announcement, languages:', targetLanguages);
              
              const { data: titleTranslations, error: titleError } = await supabase.functions.invoke('translate-content', {
                body: {
                  text: announcementForm.title,
                  fromLanguage: language,
                  toLanguages: targetLanguages
                }
              });

              setTranslationProgress(60);

              if (titleError) {
                console.error('Title translation error:', titleError);
              } else {
                console.log('Title translations received:', titleTranslations);
              }

              const { data: contentTranslations, error: contentError } = await supabase.functions.invoke('translate-content', {
                body: {
                  text: announcementForm.content,
                  fromLanguage: language,
                  toLanguages: targetLanguages
                }
              });

              setTranslationProgress(80);

              if (contentError) {
                console.error('Content translation error:', contentError);
              } else {
                console.log('Content translations received:', contentTranslations);
              }

              if (!titleError && !contentError && titleTranslations?.translations && contentTranslations?.translations) {
                for (const lang of targetLanguages) {
                  console.log(`Saving ${lang} translation for new announcement:`, {
                    title: titleTranslations.translations[lang],
                    content: contentTranslations.translations[lang]
                  });
                  
                  const { error: upsertError } = await supabase
                    .from("announcements_translations")
                    .upsert({
                      announcement_id: newAnnouncement.id,
                      language: lang,
                      title: titleTranslations.translations[lang],
                      content: contentTranslations.translations[lang],
                    }, {
                      onConflict: 'announcement_id,language'
                    });
                  
                  if (upsertError) {
                    console.error(`Error saving ${lang} translation:`, upsertError);
                  }
                }
              } else {
                console.error('Translation data incomplete:', { titleError, contentError, titleTranslations, contentTranslations });
              }
            }

            setTranslationProgress(90);

            // Save the current language translation if not English
            if (language !== 'en') {
              console.log(`Saving current language (${language}) translation for new announcement`);
              const { error: currentLangError } = await supabase
                .from("announcements_translations")
                .upsert({
                  announcement_id: newAnnouncement.id,
                  language: language,
                  title: announcementForm.title,
                  content: announcementForm.content,
                }, {
                  onConflict: 'announcement_id,language'
                });
              
              if (currentLangError) {
                console.error(`Error saving current language (${language}):`, currentLangError);
              }
            }
          } catch (translationError) {
            console.error('Translation error:', translationError);
          }
        }

        setTranslationProgress(100);
        toast({ title: t("admin.success"), description: t("admin.announcements.addedsuccess") });
        
        // Save course relationships
        await saveCourseRelationships(announcementId);
      }

      setIsDialogOpen(false);
      resetForm();
      fetchAnnouncements();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
      setTranslationProgress(0);
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!confirm(t("admin.announcements.deleteconfirm"))) return;

    try {
      const { error, count } = await supabase
        .from("announcements")
        .delete({ count: 'exact' })
        .eq("id", id);

      if (error) throw error;
      
      if (count === 0) {
        toast({
          title: t("admin.error"),
          description: "Cannot delete this announcement. You can only delete your own draft announcements.",
          variant: "destructive",
        });
        return;
      }
      
      toast({ title: t("admin.success"), description: t("admin.announcements.deletedsuccess") });
      fetchAnnouncements();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEditAnnouncement = async (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    
    // Fetch content in the current language from translations table
    try {
      const { data: translation } = await supabase
        .from("announcements_translations")
        .select("*")
        .eq("announcement_id", announcement.id)
        .eq("language", language)
        .single();
      
      if (translation) {
        setAnnouncementForm({
          title: translation.title,
          content: translation.content,
          published: announcement.published,
        });
      } else {
        // No translation exists, use original
        setAnnouncementForm({
          title: announcement.title,
          content: announcement.content,
          published: announcement.published,
        });
      }
    } catch (error) {
      // Translation doesn't exist, use original
      setAnnouncementForm({
        title: announcement.title,
        content: announcement.content,
        published: announcement.published,
      });
    }
    
    // Load course relationships
    await loadCourseRelationships(announcement.id);

    setIsDialogOpen(true);
  };

  const saveCourseRelationships = async (announcementId: string) => {
    try {
      // Delete existing relationships
      await supabase
        .from("announcement_courses")
        .delete()
        .eq("announcement_id", announcementId);

      // Insert new relationships
      if (selectedCourseIds.length > 0) {
        const relationships = selectedCourseIds.map(courseId => ({
          announcement_id: announcementId,
          course_id: courseId,
        }));

        const { error } = await supabase
          .from("announcement_courses")
          .insert(relationships);

        if (error) throw error;
      }
    } catch (error) {
      console.error("Error saving course relationships:", error);
      toast({
        title: "Warning",
        description: "Announcement saved but course links may not have been updated",
        variant: "destructive",
      });
    }
  };

  const loadCourseRelationships = async (announcementId: string) => {
    try {
      const { data } = await supabase
        .from("announcement_courses")
        .select("course_id")
        .eq("announcement_id", announcementId);
      
      if (data) {
        setSelectedCourseIds(data.map(link => link.course_id));
      }
    } catch (error) {
      console.error("Error loading course relationships:", error);
    }
  };

  const toggleCourseSelection = (courseId: string) => {
    setSelectedCourseIds(prev =>
      prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const resetForm = () => {
    setAnnouncementForm({
      title: "",
      content: "",
      published: false,
    });
    setSelectedCourseIds([]);
    setEditingAnnouncement(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t("admin.announcements.title")}</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { 
          setIsDialogOpen(open); 
          if (!open) resetForm(); 
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              {t("admin.announcements.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingAnnouncement ? t("admin.announcements.edit") : t("admin.announcements.add")}
              </DialogTitle>
              <DialogDescription>
                {editingAnnouncement
                  ? t("admin.announcements.editdesc")
                  : t("admin.announcements.adddesc")}
                {" "}
                {language === 'en' ? 'Content will be automatically translated to Russian and Spanish.' :
                 language === 'ru' ? 'Содержание будет автоматически переведено на английский и испанский.' :
                 'El contenido se traducirá automáticamente al inglés y al ruso.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">{t("admin.announcements.titlefield")}</Label>
                <Input
                  id="title"
                  value={announcementForm.title}
                  onChange={(e) =>
                    setAnnouncementForm({ ...announcementForm, title: e.target.value })
                  }
                  placeholder={t("admin.announcements.titleplaceholder")}
                />
              </div>
              <div>
                <Label htmlFor="content">{t("admin.announcements.content")}</Label>
                <Textarea
                  id="content"
                  value={announcementForm.content}
                  onChange={(e) =>
                    setAnnouncementForm({ ...announcementForm, content: e.target.value })
                  }
                  placeholder={t("admin.announcements.contentplaceholder")}
                  rows={8}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={announcementForm.published}
                  onChange={(e) =>
                    setAnnouncementForm({ ...announcementForm, published: e.target.checked })
                  }
                  className="rounded border-gray-300"
                  disabled={!isAdmin}
                />
                <Label htmlFor="published">{t("admin.announcements.published")}</Label>
              </div>
              
              {/* Course Selection */}
              {courses.length > 0 && (
                <div>
                  <Label className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    {language === 'en' ? 'Related Bible School Courses' :
                     language === 'ru' ? 'Связанные курсы библейской школы' :
                     'Cursos Relacionados de la Escuela Bíblica'}
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {language === 'en' ? 'Select courses to reference in this announcement' :
                     language === 'ru' ? 'Выберите курсы для ссылки в этом объявлении' :
                     'Seleccione cursos para referenciar en este anuncio'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {courses.map((course) => (
                      <Badge
                        key={course.id}
                        variant={selectedCourseIds.includes(course.id) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleCourseSelection(course.id)}
                      >
                        {selectedCourseIds.includes(course.id) && (
                          <X className="h-3 w-3 mr-1" />
                        )}
                        {course.title}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {isTranslating && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Translating content...' :
                       language === 'ru' ? 'Перевод контента...' :
                       'Traduciendo contenido...'}
                    </span>
                    <span className="text-muted-foreground">{translationProgress}%</span>
                  </div>
                  <Progress value={translationProgress} className="h-2" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleDialogClose} disabled={isTranslating}>
                {t("admin.announcements.cancel")}
              </Button>
              <Button onClick={handleSaveAnnouncement} disabled={isTranslating}>
                {isTranslating ? (
                  language === 'en' ? 'Translating...' :
                  language === 'ru' ? 'Перевод...' :
                  'Traduciendo...'
                ) : (
                  editingAnnouncement ? t("admin.announcements.update") : t("admin.announcements.create")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status Filter Buttons */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={statusFilter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setStatusFilter("all")}
        >
          {t("admin.bibleschool.filter.all")} ({announcements.length})
        </Button>
        <Button
          variant={statusFilter === "published" ? "default" : "outline"}
          size="sm"
          onClick={() => setStatusFilter("published")}
        >
          {t("admin.bibleschool.filter.published")} ({announcements.filter(a => a.published).length})
        </Button>
        <Button
          variant={statusFilter === "draft" ? "default" : "outline"}
          size="sm"
          onClick={() => setStatusFilter("draft")}
        >
          {t("admin.bibleschool.filter.draft")} ({announcements.filter(a => !a.published).length})
        </Button>
      </div>

      {isLoading ? (
        <div>{t("admin.announcements.loading")}</div>
      ) : filteredAnnouncements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t("admin.announcements.noannouncements")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAnnouncements.map((announcement) => {
            const translation = announcement.translations?.[language];
            const displayTitle = translation?.title || announcement.title;
            const displayContent = translation?.content || announcement.content;
            
            return (
              <Card key={announcement.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle>{displayTitle}</CardTitle>
                      <CardDescription>
                        {format(new Date(announcement.created_at), "MMM d, yyyy")} •{" "}
                        {announcement.published ? (
                          <span className="text-green-600">{t("admin.announcements.status.published")}</span>
                        ) : (
                          <span className="text-yellow-600">{t("admin.announcements.status.draft")}</span>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {(isAdmin || (isAnnouncementEditor && announcement.created_by === user?.id && !announcement.published)) && (
                        <>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleEditAnnouncement(announcement)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {displayContent.substring(0, 200)}
                    {displayContent.length > 200 && "..."}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}


    </div>
  );
};
